import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SliceCaseReducers,
} from "@reduxjs/toolkit";
import { publishEvent, saveDraftEvent } from "apis/EventServiceApi";
import { RootState } from "store/types";
import { CreateEventData } from "types";

interface CreateEditEventState {
  formData: CreateEventData;
  currentStage: number;
  isLoading: boolean;
  error: string | null;
  successMessage: string | null;
}

const initialState: CreateEditEventState = {
  formData: {
    activity_title: "",
    start_date_time: "",
    end_date_time: "",
    Event_image: "",
    activity_description: "",
    activity_goal: "",
    Country: "",
    City: "",
    State: "",
    Postcode: "",
    Details: "Default",
    GoogleUrl: "",
    influencerIds: [],
    brandIds: [],
    volunteerIds: [],
    nonProfitIds: [],
    inviteIds: [],
    causeIds: [],
    postIds:[],
    is_private: 0,
  },
  currentStage: 1,
  isLoading: false,
  error: null,
  successMessage: null,
};

export const saveDraftEventThunk = createAsyncThunk(
  "createEditEvent/saveDraftEvent",
  async (data: CreateEventData, { rejectWithValue }) => {
    try {
      const resp = await saveDraftEvent(data);
      return resp;
    } catch (error: any) {
      return rejectWithValue(error.message || "Error saving draft");
    }
  }
);

export const publishEventThunk = createAsyncThunk(
  "createEditEvent/publishEvent",
  async (data: CreateEventData, { rejectWithValue }) => {
    try {
      const resp = await publishEvent(data);
      return resp;
    } catch (error: any) {
      return rejectWithValue(error.message || "Error publishing event");
    }
  }
);

interface SetFormFieldPayload<K extends keyof CreateEventData> {
  field: K;
  value: CreateEventData[K];
}

export const createEditEventSlice = createSlice<
  CreateEditEventState,
  SliceCaseReducers<CreateEditEventState>
>({
  name: "createEditEvent",
  initialState,
  reducers: {
    setFormField<K extends keyof CreateEventData>(
      state: CreateEditEventState,
      action: PayloadAction<SetFormFieldPayload<K>>
    ) {
      const { field, value } = action.payload;
      state.formData[field] = value;
    },
    setCurrentStage(state, action: PayloadAction<number>) {
      state.currentStage = action.payload;
    },
    resetCreateEditEvent: () => initialState,

    setEntireFormData(state, action: PayloadAction<CreateEventData>) {
      state.formData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveDraftEventThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(saveDraftEventThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.successMessage = "Draft saved successfully";
        if (action.payload && typeof action.payload === "object") {
          const maybeId = (action.payload as any).id;
          if (maybeId) {
            state.formData.id = maybeId;
          }
        }
      })
      .addCase(saveDraftEventThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.successMessage = null;
      });

    // publishEvent
    builder
      .addCase(publishEventThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(publishEventThunk.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
        state.successMessage = "Event published successfully";
      })
      .addCase(publishEventThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.successMessage = null;
      });
  },
});

export const {
  setFormField,
  setCurrentStage,
  resetCreateEditEvent,
  setEntireFormData,
} = createEditEventSlice.actions;

export const selectCreateEditEvent = (state: RootState) =>
  state.createEditEvent;

export default createEditEventSlice.reducer;
